import { REPUB_CONFIG } from '@cx-web/repub-config';
import { trackEvent } from '@cx-web/utils/dataLayer';

import { type Repub } from '@cx-modules/constants';

/**
 * GA4 `dataLayer` tracking for the legacy OC login widget.
 *
 * Ported from the original `myoc-login` repo's `src/utils/ga-tracking.ts`. These events were dropped
 * in the NGF-6371 React port (merge ff3dd4a6, 2026-06-23), which stopped `account_created` and
 * `login_complete` from firing on Atlas (OC + OC-IT) — see NGF-6520 / the "Events Ga4" ticket.
 *
 * Kept LOCAL to this deletion-slated, island-safe package rather than depending on
 * `@cx-web/feature-auth` (the product-plus modal's tracking lives there). The event names and
 * parameter shape mirror the original widget so historical GA4 / CRM reports keep matching.
 *
 * Out of scope (the ported widget never renders these): social `sso_*` auth and the
 * `oc_validate_email` blur event.
 */

// All account auth events shared this UA-era category in the legacy widget; preserved so existing
// GA4 event configuration / segmentation continues to recognise them.
const EVENT_CATEGORY = 'Oddschecker Account';

const events = {
  ACCOUNT_CREATED: 'account_created',
  ACCOUNT_CREATED_ATTEMPT: 'account_created_attempt',
  ACCOUNT_CREATED_ERROR: 'account_created_error',
  LOG_IN: 'login_complete',
  LOG_IN_ATTEMPT: 'login_attempt',
  LOG_IN_ERROR: 'login_error',
} as const;

const actions = {
  LOGIN: 'Submit Normal Login',
  LOGIN_ERROR: 'Login Error',
  LOGIN_SUCCESS: 'Login Success',
  SIGN_UP: 'Submit Normal Sign Up',
  SIGN_UP_ERROR: 'Account Created Error',
  SIGN_UP_SUCCESS: 'Account Created',
} as const;

const views = {
  LOGIN: 'Login View',
  SIGN_UP: 'Sign Up View',
} as const;

const authProvider = (repub: Repub) => REPUB_CONFIG[repub]?.tracking?.authProvider;

export const trackLoginAttempt = (repub: Repub): void =>
  trackEvent(events.LOG_IN_ATTEMPT, {
    eventCategory: EVENT_CATEGORY,
    eventAction: actions.LOGIN,
    method: authProvider(repub),
    view: views.LOGIN,
  });

export const trackLoginSuccess = (repub: Repub, userId: string): void =>
  trackEvent(events.LOG_IN, {
    eventCategory: EVENT_CATEGORY,
    eventAction: actions.LOGIN_SUCCESS,
    method: authProvider(repub),
    view: views.LOGIN,
    userId,
  });

export const trackLoginError = (repub: Repub, loginError: string): void =>
  trackEvent(events.LOG_IN_ERROR, {
    eventCategory: EVENT_CATEGORY,
    eventAction: actions.LOGIN_ERROR,
    method: authProvider(repub),
    view: views.LOGIN,
    loginError,
  });

export const trackSignupAttempt = (repub: Repub): void =>
  trackEvent(events.ACCOUNT_CREATED_ATTEMPT, {
    eventCategory: EVENT_CATEGORY,
    eventAction: actions.SIGN_UP,
    method: authProvider(repub),
    view: views.SIGN_UP,
  });

export const trackSignupSuccess = (repub: Repub, userId: string): void =>
  trackEvent(events.ACCOUNT_CREATED, {
    eventCategory: EVENT_CATEGORY,
    eventAction: actions.SIGN_UP_SUCCESS,
    method: authProvider(repub),
    view: views.SIGN_UP,
    userId,
  });

export const trackSignupError = (repub: Repub, signupError: string): void =>
  trackEvent(events.ACCOUNT_CREATED_ERROR, {
    eventCategory: EVENT_CATEGORY,
    eventAction: actions.SIGN_UP_ERROR,
    method: authProvider(repub),
    view: views.SIGN_UP,
    signupError,
  });
